
import React, { useState, useEffect } from "react";
import { reportsService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { createPageUrl } from "@/utils";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Plus, 
  Filter, 
  MapPin, 
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Clock,
  Search,
  Layers
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion, AnimatePresence } from "framer-motion";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const statusColors = {
  reported: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300", icon: AlertCircle },
  acknowledged: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-300", icon: Clock },
  in_progress: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300", icon: TrendingUp },
  waiting_contractor: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", icon: Clock },
  dependent_on: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300", icon: Clock },
  resolved: { bg: "bg-green-100", text: "text-green-700", border: "border-green-300", icon: CheckCircle2 },
  closed: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300", icon: CheckCircle2 },
};

const categoryIcons = {
  pothole: "🕳️",
  blocked_drain: "🚰",
  waste_collection: "🗑️",
  street_lighting: "💡",
  flooding: "🌊",
  vandalism: "⚠️",
  illegal_dumping: "🚮",
  broken_infrastructure: "🔧",
  water_leak: "💧",
  other: "📍"
};

function MapController({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 13);
    }
  }, [center, map]);
  return null;
}

function createCustomIcon(status, category) {
  const color = {
    reported: '#ef4444',
    acknowledged: '#eab308',
    in_progress: '#3b82f6',
    dependent_on: '#a855f7',
    resolved: '#22c55e',
    closed: '#6b7280'
  }[status] || '#6b7280';

  return L.divIcon({
    html: `
      <div style="
        background: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        transition: all 0.3s ease;
      ">
        <span style="transform: rotate(45deg);">${categoryIcons[category] || '📍'}</span>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
}

// Helper function to check if a resolved/closed issue should be hidden
function shouldHideResolvedIssue(report) {
  // Only hide resolved or closed reports
  if (report.status !== 'resolved' && report.status !== 'closed') return false;
  
  // For resolved reports, use resolved_date. For closed reports, use updated_at
  let statusChangeDate;
  if (report.status === 'resolved' && report.resolved_date) {
    statusChangeDate = report.resolved_date;
  } else if (report.status === 'closed' && report.updated_at) {
    statusChangeDate = report.updated_at;
  } else {
    // Fallback to updated_at for any status change
    statusChangeDate = report.updated_at;
  }
  
  if (!statusChangeDate) return false;
  
  const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
  const changeDate = new Date(statusChangeDate);
  
  return changeDate < fiveMinutesAgo;
}

export default function MapView() {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [mapCenter, setMapCenter] = useState([-33.9249, 18.4241]); // Cape Town default
  const [currentTime, setCurrentTime] = useState(Date.now());

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: () => reportsService.list('-created_date'),
    initialData: [],
  });

  const { user } = useAuth();

  useEffect(() => {
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        () => {
          // Fallback to Cape Town
          setMapCenter([-33.9249, 18.4241]);
        }
      );
    }
  }, []);

  // Update current time every 30 seconds to re-filter resolved/closed issues
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30000); // Update every 30 seconds for more responsive hiding

    return () => clearInterval(interval);
  }, []);

  // Filter out resolved/closed issues older than 5 minutes based on currentTime
  const visibleReports = reports.filter(report => {
    const shouldHide = shouldHideResolvedIssue(report);
    if (shouldHide && (report.status === 'resolved' || report.status === 'closed')) {
      console.log(`🗺️ Hiding ${report.status} report "${report.title}" from map (older than 5 minutes)`);
    }
    return !shouldHide;
  });

  const filteredReports = visibleReports.filter(report => {
    const statusMatch = selectedStatus === "all" || report.status === selectedStatus;
    const categoryMatch = selectedCategory === "all" || report.category === selectedCategory;
    const searchMatch = !searchTerm || 
      report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.address?.toLowerCase().includes(searchTerm.toLowerCase());
    return statusMatch && categoryMatch && searchMatch;
  });

  const stats = {
    total: visibleReports.length,
    reported: visibleReports.filter(r => r.status === 'reported').length,
    in_progress: visibleReports.filter(r => r.status === 'in_progress').length,
    resolved: visibleReports.filter(r => r.status === 'resolved').length,
  };

  return (
    <div className="h-screen w-full flex flex-col bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 overflow-hidden">
      {/* Header Section - Fixed at top */}
      <div className="flex-shrink-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-b border-emerald-100/50 dark:border-gray-700 p-3 md:p-6 space-y-3 md:space-y-4 overflow-y-auto max-h-[40vh] md:max-h-none z-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white truncate">
              Community Map
            </h1>
            <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mt-1">
              Track infrastructure issues
            </p>
          </div>
          <Button
            onClick={() => navigate(createPageUrl("ReportIssue"))}
            className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg shadow-emerald-500/30 whitespace-nowrap w-full sm:w-auto"
            size="default"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5 mr-2" />
            Report Issue
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
          <Card className="p-2 md:p-3 bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 border-gray-200 dark:border-gray-600">
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3 md:w-4 md:h-4 text-gray-600 dark:text-gray-300 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-gray-600 dark:text-gray-300 truncate">Total</p>
                <p className="text-base md:text-lg font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-2 md:p-3 bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-800 border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-3 h-3 md:w-4 md:h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-red-600 dark:text-red-400 truncate">Reported</p>
                <p className="text-base md:text-lg font-bold text-red-700 dark:text-red-300">{stats.reported}</p>
              </div>
            </div>
          </Card>
          <Card className="p-2 md:p-3 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800 border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-3 h-3 md:w-4 md:h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-blue-600 dark:text-blue-400 truncate">In Progress</p>
                <p className="text-base md:text-lg font-bold text-blue-700 dark:text-blue-300">{stats.in_progress}</p>
              </div>
            </div>
          </Card>
          <Card className="p-2 md:p-3 bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-800 border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-3 h-3 md:w-4 md:h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-green-600 dark:text-green-400 truncate">Resolved</p>
                <p className="text-base md:text-lg font-bold text-green-700 dark:text-green-300">{stats.resolved}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-2 md:gap-3">
          <div className="flex-1 relative min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-full md:w-32">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="reported">Reported</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full md:w-32">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="pothole">Pothole</SelectItem>
              <SelectItem value="blocked_drain">Blocked Drain</SelectItem>
              <SelectItem value="waste_collection">Waste</SelectItem>
              <SelectItem value="street_lighting">Lighting</SelectItem>
              <SelectItem value="flooding">Flooding</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Map Container - Takes remaining space */}
      <div className="flex-1 relative min-h-0">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50">
            <div className="text-center">
              <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300 font-medium">Loading map...</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={13}
            className="w-full h-full z-0"
            zoomControl={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapController center={mapCenter} />
            <AnimatePresence>
              {filteredReports.map((report) => (
                <Marker
                  key={report.id}
                  position={[report.latitude, report.longitude]}
                  icon={createCustomIcon(report.status, report.category)}
                >
                  <Popup className="custom-popup" maxWidth={300}>
                    <div className="p-2">
                      {(() => {
                        const statusStyle = statusColors[report.status] || statusColors.reported;
                        return (
                          <>
                      <div className="flex items-start gap-3 mb-2">
                        <span className="text-2xl">{categoryIcons[report.category]}</span>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-gray-900 mb-1 text-sm">{report.title}</h3>
                          <Badge className={`${statusStyle.bg} ${statusStyle.text} text-xs`}>
                            {report.status.replace('_', ' ')}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{report.description}</p>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{report.neighborhood || report.address}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                        <TrendingUp className="w-3 h-3" />
                        <span>{report.upvote_count || 0} upvotes</span>
                        <span>•</span>
                        <span>{report.comment_count || 0} comments</span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => navigate(createPageUrl(`ReportDetails?id=${report.id}`))}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-xs"
                      >
                        View Details
                      </Button>
                          </>
                        );
                      })()}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </AnimatePresence>
          </MapContainer>
        )}

        {/* Legend - Positioned over map with proper z-index */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="absolute bottom-4 left-4 right-4 sm:right-auto p-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-lg z-[5] max-w-[calc(100vw-2rem)] sm:max-w-xs">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              <span className="font-semibold text-sm text-gray-900 dark:text-white">Status Legend</span>
            </div>
            <div className="space-y-1.5">
              {Object.entries(statusColors).filter(([status]) => status !== 'closed').map(([status, colors]) => {
                const Icon = colors.icon;
                return (
                  <div key={status} className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${colors.text.replace('text-', 'text-').replace('700', '600')} flex-shrink-0`} />
                    <span className="text-xs text-gray-600 dark:text-gray-300 capitalize">
                      {status.replace('_', ' ')}
                    </span>
                  </div>
                );
              })}
              <div className="pt-1 mt-1 border-t border-gray-200 dark:border-gray-700">
                <p className="text-[10px] text-gray-500 dark:text-gray-400 italic">
                  Closed issues auto-hide after 5 minutes
                </p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
